import { Link, router, usePage } from "@inertiajs/react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { PrintedQrCard, type StaticInvite } from "@/features/staff/printed-qr-card"
import { DYNAMIC_INVITES_FEATURE } from "@/features/staff/table-status"
import { tableTone } from "@/lib/status-colors"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
type ActiveInvite = {
    id: number
    token: string
    expiresAt: string
    maxUses: number
    usedCount: number
    url: string
}
type Table = {
    id: number
    externalId: string
    name: string
    seats: number
    status: string
    statusLabel: string
    activeBillsCount: number
    activeInvite: ActiveInvite | null
    staticInvite: StaticInvite
}
type SharedCurrentLocation = {
    id: number
    name: string
} | null
function formatDateTime(value: string) {
    return new Intl.DateTimeFormat("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(new Date(value))
}
function destroyTable(tableExternalId: string) {
    router.delete(`/manage/tables/${tableExternalId}`)
}
function createInvite(tableExternalId: string) {
    router.post("/invites", { table_id: tableExternalId })
}
function revokeInvite(inviteId: number) {
    patchInline(`/invites/${inviteId}/revoke`, { reason: "manual_staff_revoke" })
        .then((result) => toastInlineUpdateSuccess(result))
        .catch((error: unknown) => toastInlineUpdateError(error))
}
export default function StaffLocationManageTablesShow() {
    const { table, enabledFeatures = [], currentLocation } = usePage<{
        table: Table
        enabledFeatures?: string[]
        currentLocation?: SharedCurrentLocation
    }>().props
    // Off by default (#155 phase 4): the printed static QR below is the MVP's
    // door regardless, and the waiter-issued dynamic one is the opt-in extra.
    const dynamicInvitesOn = enabledFeatures.includes(DYNAMIC_INVITES_FEATURE)
    return (<StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6" data-testid="table-show-view">
          <div className="print:hidden">
          <PageHeader title={table.name} description={t("ui.react.pages.staff.location.manage.tables.show.table_restaurant")} actions={(<>
                <Button asChild variant="outline" size="sm" data-testid="back-to-tables-button">
                  <Link href="/manage/tables">{t("ui.react.pages.staff.location.manage.tables.show.back_tables")}</Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid="edit-table-button">
                  <Link href={`/manage/tables/${table.externalId}/edit`}>{t("ui.react.pages.staff.location.manage.tables.show.edit")}</Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" size="sm" data-testid="delete-table-button">{t("ui.react.pages.staff.location.manage.tables.show.delete")}</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("ui.react.pages.staff.location.manage.tables.show.delete_table")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("ui.react.pages.staff.location.manage.tables.show.action_delete_table_location")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("ui.react.pages.staff.location.manage.tables.show.cancel")}</AlertDialogCancel>
                      <AlertDialogAction variant="destructive" onClick={() => destroyTable(table.externalId)} data-testid="confirm-delete-table-button">{t("ui.react.pages.staff.location.manage.tables.show.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>)}/>
          </div>

          <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <Card className="print:hidden">
              <CardHeader className="gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle>{table.name}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.tables.show.detail_operational_table")}</CardDescription>
                  </div>
                  <StatusBadge tone={tableTone(table.status)} label={table.statusLabel}/>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border bg-card p-4 shadow-xs">
                  <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.seats")}</p>
                  <p className="mt-1 text-2xl font-semibold" data-testid="table-show-seats">{table.seats}</p>
                </div>
                <div className="rounded-lg border bg-card p-4 shadow-xs">
                  <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.status")}</p>
                  <p className="mt-1 text-lg font-semibold" data-testid="table-show-status">{table.statusLabel}</p>
                </div>
                <div className="rounded-lg border bg-card p-4 shadow-xs">
                  <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.bills_active")}</p>
                  <p className="mt-1 text-2xl font-semibold">{table.activeBillsCount}</p>
                </div>
              </CardContent>
            </Card>

            <PrintedQrCard
              tableExternalId={table.externalId}
              tableName={table.name}
              locationName={currentLocation?.name ?? ""}
              staticInvite={table.staticInvite}
            />

            {dynamicInvitesOn && (<Card className="print:hidden">
              <CardHeader>
                <CardTitle>{t("ui.react.pages.staff.location.manage.tables.show.invite_dynamic")}</CardTitle>
                <CardDescription>{t("ui.react.pages.staff.location.manage.tables.show.manage_access_qr_temporary_table")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {table.activeInvite ? (<>
                    <div className="space-y-1 rounded-lg border bg-card p-4 shadow-xs">
                      <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.url_active")}</p>
                      <p className="break-all text-sm font-medium" data-testid="invite-url">
                        {table.activeInvite.url}
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                      <div className="rounded-lg border bg-card p-4 shadow-xs">
                        <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.uses")}</p>
                        <p className="mt-1 text-lg font-semibold">
                          {table.activeInvite.usedCount}/{table.activeInvite.maxUses}
                        </p>
                      </div>
                      <div className="rounded-lg border bg-card p-4 shadow-xs">
                        <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.expires")}</p>
                        <p className="mt-1 text-sm font-medium">{formatDateTime(table.activeInvite.expiresAt)}</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                      <Button type="button" variant="outline" data-testid="reuse-invite-button" onClick={() => createInvite(table.externalId)}>{t("ui.react.pages.staff.location.manage.tables.show.reuse_invite")}</Button>
                      <Button type="button" variant="destructive" data-testid="revoke-invite-button" onClick={() => revokeInvite(table.activeInvite!.id)}>{t("ui.react.pages.staff.location.manage.tables.show.revoke_invite")}</Button>
                    </div>
                  </>) : (<>
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.show.no_active_invite")}</div>
                    <Button type="button" variant="outline" data-testid="create-invite-button" onClick={() => createInvite(table.externalId)}>{t("ui.react.pages.staff.location.manage.tables.show.generate_invite")}</Button>
                  </>)}
              </CardContent>
            </Card>)}
          </section>
        </div>
      </MainContainer>
    </StaffLayout>)
}
